/**
 * PublicWatch.tsx — Unauthenticated live-stream viewer
 *
 * Accessible at /live/:gameId — no login required.
 * Fetches game data from GET /api/games/:id/public, subscribes to live
 * score/overlay socket events, and renders the VideoPlayer with all overlays.
 * Also shows App Store + sign-up CTAs below the player.
 *
 * The game can also be supplied as a `gameId` prop instead of a route param,
 * which is how the short share-links (e.g. /SB) pin this page to one game
 * while keeping their own slug in the address bar.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import VideoPlayer from '../components/VideoPlayer';
import { mediaUrl } from '../utils/mediaUrl';

// ─────────────────────────────────────────────────────────────────────────────
// Shared timer helpers (same as Watch.tsx)
// ─────────────────────────────────────────────────────────────────────────────
interface SoccerScore {
  home_score: number; away_score: number; status: string;
  timer_running: number; timer_elapsed: number; timer_start: number | null;
  events: any[];
}

function computeElapsedMs(score: SoccerScore): number {
  const base    = score.timer_elapsed || 0;
  const running = score.timer_running === 1;
  const elapsed = base + (running ? Date.now() - (score.timer_start || 0) : 0);
  return Math.max(0, elapsed);
}

function formatTimer(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

const PERIOD_LABELS: Record<string, string> = {
  not_started:       'Pre-Game',
  first_half:        '1st Half',
  half_time:         'Half Time',
  second_half:       '2nd Half',
  full_time:         'Full Time',
  extra_time:        'ET 1st Half',
  extra_time_half:   'ET Half Time',
  extra_time_second: 'ET 2nd Half',
  extra_time_full:   'ET Full Time',
  penalties:         'Penalty Kicks',
};

const TIMER_ACTIVE = new Set(['first_half', 'second_half', 'extra_time', 'extra_time_second']);

function teamAbbr(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2 && name.length > 9) return words[0].slice(0, 6).toUpperCase();
  return name.slice(0, 6).toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// ScoreBug (self-contained copy — avoids coupling to Watch.tsx internals)
// ─────────────────────────────────────────────────────────────────────────────
const ScoreBug: React.FC<{
  homeTeam: string; awayTeam: string;
  homeLogo?: string | null; awayLogo?: string | null;
  homeScore: number; awayScore: number;
  status: string; timerMs: number;
  sponsor?: { name: string; logo_url?: string | null } | null;
}> = ({ homeTeam, awayTeam, homeLogo, awayLogo, homeScore, awayScore, status, timerMs, sponsor }) => {
  const isActive = TIMER_ACTIVE.has(status);
  const shortPeriod: Record<string, string> = {
    not_started: 'PRE', first_half: '1ST', half_time: 'HT', second_half: '2ND',
    full_time: 'FT', extra_time: 'ET1', extra_time_half: 'ETHT',
    extra_time_second: 'ET2', extra_time_full: 'ETFT', penalties: 'PKS',
  };
  const period = shortPeriod[status] ?? status.toUpperCase().slice(0, 4);

  const teamRow = (name: string, logo: string | null | undefined, score: number, isBottom: boolean) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: isBottom ? '4px 10px 6px' : '6px 10px 4px',
      borderBottom: isBottom ? 'none' : '1px solid rgba(255,255,255,0.07)',
    }}>
      {logo
        ? <img src={logo} alt="" style={{ width: 17, height: 17, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} />
        : <div style={{ width: 17, height: 17, borderRadius: 2, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
      }
      <span style={{
        flex: 1, fontSize: 11, fontWeight: 800, color: '#E8F0FE',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 68,
      }}>{teamAbbr(name)}</span>
      <span style={{ fontSize: 19, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums', lineHeight: 1, minWidth: 20, textAlign: 'right' }}>
        {score}
      </span>
    </div>
  );

  return (
    <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 20, pointerEvents: 'none', userSelect: 'none' }}>
      <div style={{
        background: 'rgba(6,8,14,0.88)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        borderRadius: 8, border: '1px solid rgba(255,255,255,0.09)', overflow: 'hidden',
        minWidth: 130, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      }}>
        {teamRow(homeTeam, homeLogo, homeScore, false)}
        {teamRow(awayTeam, awayLogo, awayScore, true)}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.04)',
          padding: '4px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: '#F97316', letterSpacing: '0.8px' }}>{period}</span>
          {isActive && <>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>·</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
              {formatTimer(timerMs)}
            </span>
          </>}
        </div>
        {sponsor && (
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.07)', padding: '3px 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.7px', whiteSpace: 'nowrap' }}>Presented by</span>
            {sponsor.logo_url
              ? <img src={mediaUrl(sponsor.logo_url)} alt={sponsor.name} style={{ height: 14, maxWidth: 60, objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              : <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{sponsor.name}</span>
            }
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EventTicker
// ─────────────────────────────────────────────────────────────────────────────
const EventTicker: React.FC<{
  events: any[]; homeTeam: string; awayTeam: string; customItems?: string[];
}> = ({ events, homeTeam, awayTeam, customItems = [] }) => {
  const statItems = events
    .filter(e => e.event_type === 'goal' || e.event_type === 'assist')
    .sort((a: any, b: any) => a.minute - b.minute)
    .map((e: any) => {
      const teamName = e.team === 'home' ? homeTeam : awayTeam;
      const icon  = e.event_type === 'goal' ? '⚽' : '🎯';
      const label = e.event_type === 'goal' ? 'Goal' : 'Assist';
      return `${icon}  ${e.minute}'  ${e.player_name || label}  (${teamName})`;
    });

  const items = [...customItems.map((t: string) => `📢  ${t}`), ...statItems];
  if (!items.length) return null;
  const content     = items.join('          ·          ');
  const durationSec = Math.max(16, items.length * 7);

  return (
    <div style={{ position: 'absolute', bottom: 52, left: 0, right: 0, zIndex: 20, pointerEvents: 'none', overflow: 'hidden' }}>
      <div style={{ background: 'rgba(5,7,12,0.84)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', borderTop: '1px solid rgba(249,115,22,0.4)', display: 'flex', alignItems: 'center' }}>
        <div style={{ background: '#F97316', padding: '5px 9px', fontSize: 9, fontWeight: 900, letterSpacing: '1.2px', color: '#fff', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>STATS</div>
        <div style={{ overflow: 'hidden', flex: 1 }}>
          <div style={{ display: 'inline-block', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600, color: '#E8F0FE', lineHeight: '28px', animation: `ticker-scroll ${durationSec}s linear infinite` }}>
            <span style={{ paddingRight: 60 }}>{content}</span>
            <span style={{ paddingRight: 60 }}>{content}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CornerLogoBug
// ─────────────────────────────────────────────────────────────────────────────
const CornerLogoBug: React.FC<{ logoUrl: string }> = ({ logoUrl }) => (
  <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, pointerEvents: 'none' }}>
    <div style={{ background: 'rgba(6,8,14,0.75)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.09)', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img src={mediaUrl(logoUrl)} alt="" style={{ height: 32, maxWidth: 80, objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Apple App Store SVG badge (official proportions, all-SVG — no external image)
// ─────────────────────────────────────────────────────────────────────────────
const AppStoreBadge: React.FC<{ href: string }> = ({ href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', lineHeight: 0 }}>
    <svg width="140" height="42" viewBox="0 0 140 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="140" height="42" rx="7" fill="#000"/>
      <rect width="140" height="42" rx="7" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"/>
      {/* Apple logo */}
      <path d="M21.5 10.5c1.2-1.4 2-3.3 1.8-5.2-1.7.1-3.8 1.2-5 2.6-1.1 1.2-2 3.2-1.8 5 2 .2 3.9-.9 5-2.4z" fill="white"/>
      <path d="M23.3 13.3c-2.8-.2-5.1 1.6-6.4 1.6-1.3 0-3.3-1.5-5.4-1.5-2.8.1-5.4 1.7-6.8 4.2-2.9 5 -.8 12.4 2 16.5 1.4 2 3 4.2 5.2 4.1 2-.1 2.8-1.3 5.2-1.3 2.4 0 3.1 1.3 5.2 1.3 2.2 0 3.7-2 5-4 1.6-2.3 2.2-4.5 2.3-4.6-.1 0-4.4-1.7-4.4-6.6 0-4.2 3.4-6.2 3.6-6.3-2-2.9-5-3.4-5.5-3.4z" fill="white"/>
      {/* "Download on the" text */}
      <text x="36" y="16" fill="white" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="8" fontWeight="400" letterSpacing="0.2">Download on the</text>
      {/* "App Store" text */}
      <text x="35" y="30" fill="white" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="17" fontWeight="600" letterSpacing="-0.3">App Store</text>
    </svg>
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const PublicWatch: React.FC<{ gameId?: string }> = ({ gameId: fixedGameId }) => {
  const { gameId: routeGameId } = useParams<{ gameId: string }>();
  // A pinned prop wins over the route param, so short slugs can reuse this page.
  const gameId = fixedGameId ?? routeGameId;

  const [game,    setGame]    = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // Live overlay state (from socket)
  const [overlayOn,    setOverlayOn]    = useState(false);
  const [tickerOn,     setTickerOn]     = useState(false);
  const [customTicker, setCustomTicker] = useState<string[]>([]);
  const [cornerLogo,   setCornerLogo]   = useState<string | null>(null);
  const [overlaySponsor, setOverlaySponsor] = useState<any | null>(null);
  const [score,        setScore]        = useState<any | null>(null);

  // Soccer live clock
  const [timerMs,   setTimerMs]   = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tickTimer = useCallback((sc: SoccerScore) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (TIMER_ACTIVE.has(sc.status) && sc.timer_running) {
      timerRef.current = setInterval(() => setTimerMs(computeElapsedMs(sc)), 500);
    } else {
      setTimerMs(computeElapsedMs(sc));
    }
  }, []);

  // ── Fetch game data (no auth) ─────────────────────────────────────────────
  useEffect(() => {
    if (!gameId) return;

    const API_BASE = process.env.REACT_APP_API_URL ?? '/api';
    fetch(`${API_BASE}/games/${gameId}/public`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: any) => {
        setGame(data);
        setOverlayOn(!!data.overlay_enabled);
        setTickerOn(!!data.ticker_enabled);
        setCustomTicker(data.custom_ticker ?? []);
        setCornerLogo(data.corner_logo_url ?? null);
        setOverlaySponsor(data.overlay_sponsor ?? null);
        if (data.score) {
          setScore(data.score);
          if (data.sport === 'soccer') tickTimer(data.score as SoccerScore);
        }
      })
      .catch(() => setError('Game not found or no longer available.'))
      .finally(() => setLoading(false));

    // ── Socket.io — live score / overlay pushes ───────────────────────────
    const SERVER_URL = process.env.REACT_APP_SERVER_URL ?? '';
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });

    let sessionId = sessionStorage.getItem('viewer_session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('viewer_session_id', sessionId);
    }

    const joinRoom = () => socket.emit('join_game', { gameId, sessionId });
    socket.on('connect', joinRoom);
    joinRoom();

    socket.on('score_update', (data: any) => {
      if (data.gameId === gameId) {
        setScore(data.score);
        if (data.sport === 'soccer') tickTimer(data.score as SoccerScore);
      }
    });

    socket.on('overlay_update', (data: any) => {
      if (data.gameId === gameId) {
        setOverlayOn(!!data.overlay_enabled);
        setTickerOn(!!data.ticker_enabled);
        setOverlaySponsor(data.sponsor ?? null);
        if (data.custom_ticker !== undefined) {
          try { setCustomTicker(JSON.parse(data.custom_ticker || '[]')); } catch { setCustomTicker([]); }
        }
        if (data.corner_logo_url !== undefined) setCornerLogo(data.corner_logo_url ?? null);
      }
    });

    socket.on('game_completed', (data: any) => {
      if (data.gameId === gameId) setGame((g: any) => g ? { ...g, status: 'completed' } : g);
    });

    return () => {
      socket.emit('leave_game', { gameId, sessionId });
      socket.disconnect();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameId, tickTimer]);

  // ── Build video URL ───────────────────────────────────────────────────────
  // For completed games with a recording: play the recording.
  // For live / upcoming games: play the HLS stream.
  const HLS_BASE   = process.env.REACT_APP_HLS_URL ?? '/hls';
  const recording  = game?.recording ?? null;
  const hasRecording = game?.status === 'completed' && recording?.file_path;

  const videoUrl = hasRecording ? (() => {
    // Prefer compressed copy if one exists; fall back to raw recording.
    const fp = (recording.compressed_path as string | null) || (recording.file_path as string);
    // Wasabi proxy paths (/api/…) and full http URLs are used as-is.
    // Local NMS recordings (/live/…) are served through the /hls nginx proxy.
    const raw = (fp.startsWith('/api/') || fp.startsWith('http')) ? fp : `${HLS_BASE}${fp}`;
    return mediaUrl(raw);
  })() : game ? mediaUrl(`${HLS_BASE}/live/${game.stream_key}/index.m3u8`) : '';

  const isSoccer = game?.sport === 'soccer';
  const soccerScore = score as SoccerScore | null;

  // Period offset for display minute
  const st = soccerScore?.status ?? '';
  const periodOffset = st === 'second_half' ? 45 : st === 'extra_time' ? 90 : st === 'extra_time_second' ? 105 : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#05070c', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: '#8899aa' }}>
      <div style={{ fontSize: 40 }}>📡</div>
      <div style={{ fontSize: 15 }}>Loading stream…</div>
    </div>
  );

  if (error || !game) return (
    <div style={{ minHeight: '100vh', background: '#05070c', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: '#8899aa', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>📡</div>
      <div style={{ color: '#fff', fontWeight: 600 }}>Stream Not Found</div>
      <div style={{ fontSize: 14 }}>{error}</div>
      <a href="https://www.slowrib.com" style={{ color: '#F97316', fontWeight: 600, marginTop: 8 }}>← Go to Slow Rib Sports</a>
    </div>
  );

  const sportLabel = { baseball: '⚾ Baseball', softball: '🥎 Softball', soccer: '⚽ Soccer', basketball: '🏀 Basketball' }[game.sport as string] ?? game.sport;

  return (
    <div style={{ minHeight: '100vh', background: '#05070c', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0 40px' }}>

      {/* ── Top bar ── */}
      <div style={{ width: '100%', background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box' }}>
        <a href="https://www.slowrib.com" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: '#F97316', letterSpacing: '-0.5px', fontFamily: 'system-ui, sans-serif' }}>Slow Rib</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase' }}>Sports</span>
        </a>
        {/* Live indicator */}
        {game.status !== 'completed' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '4px 10px' }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#EF4444', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', letterSpacing: '0.8px' }}>LIVE</span>
          </div>
        )}
      </div>

      {/* ── Game title ── */}
      <div style={{ width: '100%', maxWidth: 900, padding: '18px 20px 12px', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#F97316', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 4 }}>{sportLabel}</div>
        <h1 style={{ margin: 0, fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{game.title}</h1>
        <div style={{ marginTop: 4, fontSize: 14, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
          {game.home_team} <span style={{ color: 'rgba(255,255,255,0.2)' }}>vs</span> {game.away_team}
        </div>
      </div>

      {/* ── Replay banner (shown when watching a completed game recording) ── */}
      {hasRecording && (
        <div style={{
          width: '100%', maxWidth: 900, padding: '0 20px 12px', boxSizing: 'border-box',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: 'rgba(5,62,194,0.15)', border: '1px solid rgba(5,62,194,0.4)',
            borderRadius: 8, padding: '7px 14px',
            fontSize: 13, fontWeight: 700, color: '#93b4ff',
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <path d="M3 2.5l9 4.5-9 4.5V2.5z"/>
            </svg>
            Game replay — Full Time
          </div>
        </div>
      )}

      {/* ── DEBUG (temporary) ── */}
      <div style={{ width: '100%', maxWidth: 900, padding: '0 20px 8px', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
          status={game.status} | hasRec={String(!!hasRecording)} | url={videoUrl.slice(0, 60)}
        </div>
      </div>

      {/* ── Video player ── */}
      <div style={{ width: '100%', maxWidth: 900, padding: '0 20px', boxSizing: 'border-box' }}>
        <VideoPlayer streamUrl={videoUrl}>

          {/* Score bug */}
          {overlayOn && isSoccer && soccerScore && (
            <ScoreBug
              homeTeam={game.home_team}
              awayTeam={game.away_team}
              homeLogo={mediaUrl(game.home_logo_url ?? null) || null}
              awayLogo={mediaUrl(game.away_logo_url ?? null) || null}
              homeScore={soccerScore.home_score ?? 0}
              awayScore={soccerScore.away_score ?? 0}
              status={soccerScore.status ?? 'not_started'}
              timerMs={timerMs}
              sponsor={overlaySponsor}
            />
          )}

          {/* Stats ticker */}
          {tickerOn && isSoccer && soccerScore && (
            <EventTicker
              events={soccerScore.events ?? []}
              homeTeam={game.home_team}
              awayTeam={game.away_team}
              customItems={customTicker}
            />
          )}

          {/* Corner logo */}
          {cornerLogo && <CornerLogoBug logoUrl={cornerLogo} />}

        </VideoPlayer>
      </div>

      {/* ── Download / Sign-up CTAs ── */}
      <div style={{
        width: '100%', maxWidth: 900, padding: '28px 20px 0', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
      }}>
        {/* Divider */}
        <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.07)' }} />

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 16, letterSpacing: '0.3px' }}>
            Get the full experience — live scores, stats &amp; highlights
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
            {/* App Store badge */}
            <AppStoreBadge href="https://apps.apple.com/us/app/slow-rib-sports/id6760488944" />

            {/* Sign up link — styled as a secondary button */}
            <a
              href="https://www.slowrib.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display:        'inline-flex',
                alignItems:     'center',
                gap:            8,
                padding:        '10px 20px',
                background:     'rgba(249,115,22,0.12)',
                border:         '1px solid rgba(249,115,22,0.4)',
                borderRadius:   8,
                color:          '#F97316',
                fontWeight:     700,
                fontSize:       14,
                textDecoration: 'none',
                letterSpacing:  '-0.2px',
                whiteSpace:     'nowrap',
                transition:     'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(249,115,22,0.2)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(249,115,22,0.7)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(249,115,22,0.12)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(249,115,22,0.4)'; }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="5" r="3"/>
                <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
              </svg>
              Create Free Account
            </a>
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', textAlign: 'center', lineHeight: 1.6 }}>
          © {new Date().getFullYear()} Slow Rib Sports · <a href="https://www.slowrib.com/privacy" style={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }}>Privacy</a> · <a href="https://www.slowrib.com/terms" style={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }}>Terms</a>
        </div>
      </div>

      {/* Pulse animation for live dot */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.35; }
        }
      `}</style>
    </div>
  );
};

export default PublicWatch;
